import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/hooks/useLanguage';
import { languages } from '@/i18n';

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  type?: 'website' | 'article' | 'product';
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
  };
}

const SITE_NAME = 'Postnummerservice';
const BASE_URL = 'https://postnummerservice.se';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

export function SEO({
  title,
  description,
  path,
  type = 'website',
  image,
  noindex = false,
  jsonLd,
  article,
}: SEOProps) {
  const { lang } = useLanguage();

  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const canonicalPath = path || '';
  const canonicalUrl = `${BASE_URL}/${lang}${canonicalPath}`;
  const ogImage = image || DEFAULT_IMAGE;

  // Generate hreflang alternates
  const hreflangLinks = languages.map((l) => ({
    rel: 'alternate',
    hrefLang: l.code === 'sv' ? 'sv-SE' : l.code === 'da' ? 'da-DK' : l.code === 'fi' ? 'fi-FI' : l.code === 'no' ? 'nb-NO' : 'en',
    href: `${BASE_URL}/${l.code}${canonicalPath}`,
  }));

  // Add x-default (Swedish)
  hreflangLinks.push({
    rel: 'alternate',
    hrefLang: 'x-default',
    href: `${BASE_URL}/sv${canonicalPath}`,
  });

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang */}
      {hreflangLinks.map((link) => (
        <link
          key={link.hrefLang}
          rel={link.rel}
          hrefLang={link.hrefLang}
          href={link.href}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'sv' ? 'sv_SE' : lang === 'da' ? 'da_DK' : lang === 'fi' ? 'fi_FI' : lang === 'no' ? 'nb_NO' : 'en_GB'} />

      {/* Article meta */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Noindex */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

// Reusable JSON-LD schemas
export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Postnummerservice Norden AB',
    url: BASE_URL,
    logo: `${BASE_URL}/ps-logo.png`,
    description: 'Kvalitetssäkrade adress- och postnummerdata för Sverige och Norden',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Biblioteksgatan 29',
      addressLocality: 'Stockholm',
      postalCode: '114 35',
      addressCountry: 'SE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+46-8-410-239-70',
      contactType: 'customer service',
      email: 'info@postnummerservice.se',
      availableLanguage: ['Swedish', 'English', 'Danish', 'Finnish', 'Norwegian'],
    },
    sameAs: [
      'https://www.linkedin.com/company/postnummerservice',
    ],
  };
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    inLanguage: ['sv', 'en', 'da', 'fi', 'no'],
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/sv/shop?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function getFAQSchema(questions: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };
}

export function getProductSchema(product: {
  name: string;
  description: string;
  price: number;
  currency: string;
  sku?: string | null;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    sku: product.sku || undefined,
    url: product.url,
    brand: {
      '@type': 'Brand',
      name: 'Postnummerservice',
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: product.currency,
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Postnummerservice Norden AB',
      },
    },
  };
}

export function getArticleSchema(article: {
  headline: string;
  datePublished: string;
  dateModified?: string;
  url: string;
  description?: string;
}) {
  const org = {
    '@type': 'Organization',
    name: 'Postnummerservice Norden AB',
    url: BASE_URL,
    logo: `${BASE_URL}/ps-logo.png`,
  };
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    datePublished: article.datePublished,
    ...(article.dateModified && { dateModified: article.dateModified }),
    ...(article.description && { description: article.description }),
    author: org,
    publisher: org,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url,
    },
  };
}

export function getLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Postnummerservice Norden AB',
    url: BASE_URL,
    logo: `${BASE_URL}/ps-logo.png`,
    description: 'Kvalitetssäkrade adress- och postnummerdata för Sverige och Norden',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Biblioteksgatan 29',
      addressLocality: 'Stockholm',
      postalCode: '114 35',
      addressCountry: 'SE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 59.3389,
      longitude: 18.0747,
    },
    email: 'info@postnummerservice.se',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'info@postnummerservice.se',
      availableLanguage: ['Swedish', 'English', 'Danish', 'Finnish', 'Norwegian'],
    },
  };
}

export function getPlaceSchema(place: {
  name: string;
  description: string;
  url: string;
  containedIn?: string;
  population?: number;
  area?: number;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'AdministrativeArea',
    name: place.name,
    description: place.description,
    url: place.url,
    ...(place.containedIn && { containedInPlace: { '@type': 'AdministrativeArea', name: place.containedIn } }),
    additionalProperty: [
      ...(place.population != null ? [{
        '@type': 'PropertyValue',
        name: 'population',
        value: place.population,
      }] : []),
      ...(place.area != null ? [{
        '@type': 'PropertyValue',
        name: 'area',
        unitCode: 'KMK',
        value: place.area,
      }] : []),
    ],
  };
}
